import React from "react";
import { Ring } from "@uiball/loaders";
import { ExternalLink } from "lucide-react";
import {
  HistoryItem,
  HistoryItemHeader,
  HistoryItemTitle,
  HistoryItemDate,
  HistorySection,
  HistorySectionTitle,
  HashContainer,
  HashText,
  HashIcon,
  DocumentsContainer,
  DocumentCard,
  DocumentIcon,
  DocumentInfo,
  DocumentName,
  DocumentHint,
  DocumentArrow,
} from "./TraceabilityStyles";
import { DescriptionSidebar, SidebarOverlay, SidebarHeader, SidebarTitle, CloseButton } from "./FormStyles";

const shortHash = (h) => {
  if (!h) return '—';
  if (h.length <= 5) return h;
  // Show first 3 chars, ellipsis, and last 2 chars: "123...ed"
  return `${h.slice(0, 3)}...${h.slice(-2)}`;
};

const TrackingHistorySidebar = ({
  isOpen,
  onClose,
  isLoading,
  trackingHistory,
}) => {
  return (
    <>
      <SidebarOverlay show={isOpen} onClick={onClose} />
      <DescriptionSidebar open={isOpen}>
        <SidebarHeader>
          <SidebarTitle>Tracking History</SidebarTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </SidebarHeader>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Ring size={40} color="#00f7d2" />
          </div>
        ) : (
          <>
            {/* Descriptions Section */}
            {trackingHistory.descriptions.length > 0 && (
              <HistorySection>
                <HistorySectionTitle>
                  <span>📝</span>
                  Descriptions ({trackingHistory.descriptions.length})
                </HistorySectionTitle>
                {trackingHistory.descriptions.map((item, index) => (
                  <HistoryItem key={item._id || index}>
                    <HistoryItemHeader>
                      <div>
                        <HistoryItemTitle>Description #{index + 1}</HistoryItemTitle>
                        <HistoryItemDate>
                          {item.createdAt 
                            ? new Date(item.createdAt).toLocaleString()
                            : 'N/A'}
                        </HistoryItemDate>
                      </div>
                      {item.hash && (
                        <HashContainer>
                          <HashText style={{ fontFamily: 'monospace', color: '#00f7d2' }}>
                            {shortHash(item.hash)}
                          </HashText>
                          <HashIcon
                            onClick={() => window.open(`https://bscscan.com/tx/${item.hash}`, '_blank', 'noopener,noreferrer')}
                            title="Open in BSCScan"
                          >
                            <ExternalLink size={14} />
                          </HashIcon>
                        </HashContainer>
                      )}
                    </HistoryItemHeader>

                    <div style={{ fontSize: 14, color: '#e5e7eb', lineHeight: 1.6 }}>
                      {item.description && (
                        <div style={{ marginBottom: 8 }}>
                          <strong style={{ color: '#9ca3af', fontSize: 12 }}>Description:</strong>
                          <div style={{ marginTop: 4 }}>{item.description}</div>
                        </div>
                      )}
                      {item.origin && (
                        <div style={{ marginBottom: 8 }}>
                          <strong style={{ color: '#9ca3af', fontSize: 12 }}>Origin:</strong>
                          <div style={{ marginTop: 4 }}>{item.origin}</div>
                        </div>
                      )}
                      {item.location && (
                        <div style={{ marginBottom: 8 }}>
                          <strong style={{ color: '#9ca3af', fontSize: 12 }}>Location:</strong>
                          <div style={{ marginTop: 4 }}>{item.location}</div>
                        </div>
                      )}
                    </div>

                    {item.documents && item.documents.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Files</div>
                        <DocumentsContainer>
                          {item.documents.map((document, docIndex) => (
                            <DocumentCard
                              key={docIndex}
                              onClick={() => window.open(document, '_blank')}
                            >
                              <DocumentIcon>📄</DocumentIcon>
                              <DocumentInfo>
                                <DocumentName>
                                  {`File ${docIndex + 1}`}
                                </DocumentName>
                                <DocumentHint>
                                  Click to open
                                </DocumentHint>
                              </DocumentInfo>
                              <DocumentArrow>→</DocumentArrow>
                            </DocumentCard>
                          ))}
                        </DocumentsContainer>
                      </div>
                    )}
                  </HistoryItem>
                ))}
              </HistorySection>
            )}

            {/* Certificates Section */}
            {trackingHistory.certificates.length > 0 && (
              <HistorySection>
                <HistorySectionTitle>
                  <span>🏆</span>
                  Certificates ({trackingHistory.certificates.length})
                </HistorySectionTitle>
                {trackingHistory.certificates.map((item, index) => (
                  <HistoryItem key={item._id || index}>
                    <HistoryItemHeader>
                      <div>
                        <HistoryItemTitle>{item.certificate_name || `Certificate ${index + 1}`}</HistoryItemTitle>
                        <HistoryItemDate>
                          {item.createdAt 
                            ? new Date(item.createdAt).toLocaleString()
                            : 'N/A'}
                        </HistoryItemDate>
                      </div>
                      {item.hash && (
                        <HashContainer>
                          <HashText style={{ fontFamily: 'monospace', color: '#00f7d2' }}>
                            {shortHash(item.hash)}
                          </HashText>
                          <HashIcon
                            onClick={() => window.open(`https://bscscan.com/tx/${item.hash}`, '_blank', 'noopener,noreferrer')}
                            title="Open in BSCScan"
                          >
                            <ExternalLink size={14} />
                          </HashIcon>
                        </HashContainer>
                      )}
                    </HistoryItemHeader>

                    <div style={{ fontSize: 14, color: '#e5e7eb', lineHeight: 1.6 }}>
                      {item.location && (
                        <div style={{ marginBottom: 8 }}>
                          <strong style={{ color: '#9ca3af', fontSize: 12 }}>Location:</strong>
                          <div style={{ marginTop: 4 }}>{item.location}</div>
                        </div>
                      )}
                    </div>

                    {item.documents && item.documents.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>Certificate Files</div>
                        <DocumentsContainer>
                          {item.documents.map((document, docIndex) => (
                            <DocumentCard
                              key={docIndex}
                              onClick={() => window.open(document, '_blank')}
                            >
                              <DocumentIcon>📄</DocumentIcon>
                              <DocumentInfo>
                                <DocumentName>
                                  {item.certificate_name || `Certificate File ${docIndex + 1}`}
                                </DocumentName>
                                <DocumentHint>
                                  Click to open
                                </DocumentHint>
                              </DocumentInfo>
                              <DocumentArrow>→</DocumentArrow>
                            </DocumentCard>
                          ))}
                        </DocumentsContainer>
                      </div>
                    )}
                  </HistoryItem>
                ))}
              </HistorySection>
            )}

            {trackingHistory.descriptions.length === 0 && trackingHistory.certificates.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                No tracking history available for this record.
              </div>
            )}
          </>
        )}
      </DescriptionSidebar>
    </>
  );
};

export default TrackingHistorySidebar;
